import { useState } from 'react';
import { toast } from 'sonner';
import { UploadSimple, Spinner } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { folderOptions, folderPathLabel } from './folderTree';
import { dmsApi } from './dmsApi';
import { UploadFilePreview } from './UploadFilePreview';

const Sel = ({ value, onChange, options, testId }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
    className="h-9 w-full rounded-md border border-zinc-300 bg-white px-2 text-sm text-zinc-800 outline-none focus:ring-2 focus:ring-teal-200">
    {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>
);

// D1/D2/D3 — 保存する前に必ず照合する。同じ指紋のファイル、同じ図番の書類、
// 種類の推定を見せて、人に3択を選ばせる（黙って版を足さない・黙って重複を作らない）。
async function sha256Hex(file) {
  try {
    const buf = await file.arrayBuffer();
    const digest = await crypto.subtle.digest('SHA-256', buf);
    return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
  } catch { return null; }
}

export default function DocUploadDialog({ meta, folders, defaultFolderId, onClose, onSaved }) {
  const [check, setCheck] = useState(null);
  const [checking, setChecking] = useState(false);
  const [attachTo, setAttachTo] = useState('');
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [folderId, setFolderId] = useState(defaultFolderId || folders[0]?.id || '');
  const [docType, setDocType] = useState('other');
  const [status, setStatus] = useState('draft');
  const [conf, setConf] = useState('internal');
  const [source, setSource] = useState('upload');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [saving, setSaving] = useState(false);

  const runCheck = async (f) => {
    setChecking(true);
    try {
      const sha256 = await sha256Hex(f);
      const r = await dmsApi.uploadCheck({ filename: f.name, size: f.size, sha256,
        folder_id: folderId || null });
      setCheck(r);
      if (r.suggested_type?.doc_type && r.suggested_type.doc_type !== 'other') {
        setDocType(r.suggested_type.doc_type);
      }
      if (r.drawing?.number) setTitle(r.drawing.number);   // 図番は版記号まで残す
    } catch { setCheck(null); } finally { setChecking(false); }
  };

  const addAsVersion = async () => {
    if (!attachTo) { toast.error('どの書類の新しい版にするか選んでください'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('change_note', `${file.name} を新しい版として追加`);
      const r = await dmsApi.replaceFile(attachTo, fd);
      toast.success(`新しい版として追加しました（第${r.version_number}版）`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '版の追加に失敗しました');
    } finally { setSaving(false); }
  };

  const submit = async () => {
    if (!file) { toast.error('ファイルを選択してください'); return; }
    if (!title.trim()) { toast.error('タイトルを入力してください'); return; }
    if (!folderId) { toast.error('フォルダーを選択してください'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('title', title.trim());
      fd.append('folder_id', folderId);
      fd.append('doc_type', docType);
      fd.append('status', status);
      fd.append('confidentiality', conf);
      fd.append('source_type', source);
      fd.append('description', description.trim());
      fd.append('tags', tags);
      const doc = await dmsApi.upload(fd);
      toast.success(`「${doc.title}」を登録しました（${doc.document_number}）`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'アップロードに失敗しました');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="dms-upload-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <UploadSimple size={20} weight="duotone" className="text-teal-600" /> 書類アップロード
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <label className="flex items-center gap-2 rounded-lg border border-dashed border-zinc-300 px-3 py-4 text-sm text-zinc-500 cursor-pointer hover:bg-zinc-50 hover:border-teal-300 transition-colors">
            <UploadSimple size={18} />
            {file ? <span className="text-zinc-800 font-medium truncate">{file.name}</span> : 'ファイルを選択（PDF・画像・Office・CAD、100MBまで）'}
            <input type="file" className="hidden" data-testid="dms-upload-file"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) { setFile(f); if (!title) setTitle(f.name.replace(/\.[^.]+$/, '')); runCheck(f); }
              }} />
          </label>
          {file && <UploadFilePreview file={file} />}
          {checking && <p className="text-[11px] text-zinc-500" data-testid="dms-upload-checking">既存の書類と照合しています…</p>}
          {check && (check.duplicates.length > 0 || check.revision_candidates.length > 0) && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-2.5 space-y-2"
              data-testid="dms-upload-conflict">
              <p className="text-xs font-semibold text-amber-900">
                {check.duplicates.length > 0 ? '同じファイルが既に登録されています' : '同じ図番の書類があります'}
              </p>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {[...check.duplicates, ...check.revision_candidates].map((c) => (
                  <label key={c.document_id} className="flex items-start gap-2 text-[11px] text-zinc-700">
                    <input type="radio" name="attach" value={c.document_id} className="mt-0.5 accent-teal-600"
                      checked={attachTo === c.document_id}
                      onChange={() => setAttachTo(c.document_id)}
                      data-testid={`dms-upload-conflict-${c.document_id}`} />
                    <span className="min-w-0">
                      <span className="font-medium">{c.title}</span>
                      <span className="text-zinc-400"> {c.document_number}</span>
                      {c.rev && <span className="ml-1 rounded bg-white px-1 text-[10px] text-zinc-600">{c.rev}版</span>}
                      {c.reason_ja && <span className="block text-zinc-500">{c.reason_ja}</span>}
                    </span>
                  </label>
                ))}
              </div>
              <div className="flex flex-wrap gap-1.5">
                <Button size="sm" className="h-8 text-xs bg-teal-700 hover:bg-teal-800"
                  disabled={saving || !attachTo} onClick={addAsVersion}
                  data-testid="dms-upload-choice-new-version">新しい版として追加</Button>
                <Button size="sm" variant="outline" className="h-8 text-xs"
                  disabled={saving} onClick={submit}
                  data-testid="dms-upload-choice-separate">別書類として保存</Button>
                <Button size="sm" variant="ghost" className="h-8 text-xs text-zinc-500"
                  disabled={saving} onClick={onClose}
                  data-testid="dms-upload-choice-skip">スキップ</Button>
              </div>
            </div>
          )}
          {check?.suggested_type?.doc_type && check.suggested_type.doc_type !== 'other' && (
            <p className="text-[11px] text-teal-800 bg-teal-50 border border-teal-200 rounded-md px-2 py-1"
              data-testid="dms-upload-type-guess">
              種類の推定: {check.suggested_type.label_ja}（確度 {Math.round((check.suggested_type.confidence || 0) * 100)}%・{check.suggested_type.reason_ja}）
            </p>
          )}
          <div>
            <Label className="text-xs font-semibold text-zinc-600">タイトル <span className="text-rose-500">＊</span></Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200}
              className="h-9 mt-1" data-testid="dms-upload-title" />
          </div>
          <div>
            <Label className="text-xs font-semibold text-zinc-600">フォルダー <span className="text-rose-500">＊</span></Label>
            <div className="mt-1">
              <Sel value={folderId} onChange={setFolderId} testId="dms-upload-folder"
                options={folderOptions(folders)} />
              {folderId && (
                <p className="mt-1 text-[11px] text-zinc-500" data-testid="dms-upload-folder-path">
                  保存先: {folderPathLabel(folders, folderId)}
                </p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold text-zinc-600">書類種別</Label>
              <div className="mt-1"><Sel value={docType} onChange={setDocType} options={meta.doc_types} testId="dms-upload-type" /></div>
            </div>
            <div>
              <Label className="text-xs font-semibold text-zinc-600">ステータス</Label>
              <div className="mt-1"><Sel value={status} onChange={setStatus} testId="dms-upload-status"
                options={meta.statuses.filter((s) => !['archived', 'obsolete'].includes(s.value))} /></div>
            </div>
            <div>
              <Label className="text-xs font-semibold text-zinc-600">機密区分</Label>
              <div className="mt-1"><Sel value={conf} onChange={setConf} options={meta.confidentiality} testId="dms-upload-conf" /></div>
            </div>
            <div>
              <Label className="text-xs font-semibold text-zinc-600">取得元</Label>
              <div className="mt-1"><Sel value={source} onChange={setSource} options={meta.source_types} testId="dms-upload-source" /></div>
            </div>
          </div>
          {source === 'odoo_import' && (
            <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5"
              data-testid="dms-upload-archive-note">
              旧Odoo取込の書類は自動的に「アーカイブ・読取専用・実績集計除外」として保護されます
            </p>
          )}
          <div>
            <Label className="text-xs font-semibold text-zinc-600">説明（任意）</Label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} maxLength={2000}
              className="h-9 mt-1" data-testid="dms-upload-desc" />
          </div>
          <div>
            <Label className="text-xs font-semibold text-zinc-600">タグ（カンマ区切り・任意）</Label>
            <Input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="例: 2026, 検査, A社"
              className="h-9 mt-1" data-testid="dms-upload-tags" />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="dms-upload-cancel">キャンセル</Button>
          <Button onClick={submit} disabled={saving} className="bg-teal-700 hover:bg-teal-800" data-testid="dms-upload-submit">
            {saving ? <><Spinner size={14} className="animate-spin mr-1" />保存中...</> : 'アップロード'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
